import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { Swiper } from "swiper/react";
import { SwiperSlide } from "swiper/react";
import { isDesktopMediaQuery } from '../../constants/constants';
import { fetchInfluencerContent, markExpired, setLoading } from '../../redux-stores/slices/influencerContentSlice';
import { RootState, AppDispatch } from '../../redux-stores/store';
import { MediaType, Post } from '../../types/influencer/influencerTypes';
import { DMAutomation } from '../../types/automations/dmAutomationTypes';
import AddLink from './AddLink';
import { POSTAPIError } from '../../types';
import { ERROR } from '../../constants/errors';
import InsufficientPermissionsCTAModal from '../CTA/InsufficientPermissionsCTAModal';
import DMAutomationsSkeleton from '../../skeletons/DMAutomationsSkeleton';
import { fetchLatestInfluencerMedia, fetchLatestInfluencerSocialPosts } from '../../services/api/influencer';
import { toast } from 'react-toastify';
import { getErrorMessage } from '../../utils/helpers';
import { fetchAvailablePostsThunk } from '../../redux-stores/slices/dmAutomationsListSlice';
import { SocialPost } from '../../types/collaborations/CollaborationTypes';

interface AvailableToLinkProps {

}

const AvailableToLink: React.FC<AvailableToLinkProps> = () => {

    const selectedSocialAccount = useSelector((state: RootState) => state.dmAutomationsList.selectedSocialAccount);

    const [linkingError, setLinkingError] = useState<POSTAPIError | null>(null);

    const isDesktop = useMediaQuery(isDesktopMediaQuery);

    const loading = useSelector((state: RootState) => state.dmAutomationsList.availableToLink.loading);


    const [selectedPost, setSelectedPost] = useState<null | SocialPost>(null);


    const posts = useSelector((state: RootState) => state.dmAutomationsList.availableToLink[selectedSocialAccount?.social_account?.uuid ?? '']?.items ?? []);
    const dispatch = useDispatch<AppDispatch>();


    const fetchAvailablePosts = useCallback(async (force=false) => {
        if(!selectedSocialAccount) return;
        dispatch(fetchAvailablePostsThunk({socialAccountId: selectedSocialAccount.social_account?.uuid, force}));
    }, [dispatch, selectedSocialAccount])

    useEffect(() => {
        fetchAvailablePosts();
    }, [fetchAvailablePosts, selectedSocialAccount])

    const fetchLatestMedia = async (): Promise<void> => {
        if(!selectedSocialAccount) return;
        try {
            dispatch(setLoading(true));
            await fetchLatestInfluencerSocialPosts(selectedSocialAccount.social_account?.uuid);
        } catch (error: any) {
            console.log(error);
            toast.error(getErrorMessage(error));
        } finally {
            dispatch(markExpired());
            fetchAvailablePosts(true);
        }
    }


    if (loading) return <DMAutomationsSkeleton />

    return (

        <div>
            <div className="d-flex justify-content-between align-items-center mx-3 mb-3">
                <h4 className="pop ">Recent posts available for linking</h4>
                <button type="button" className="btn btn-xxs ms-2 btn-primary rounded-sm" title="Fetch Latest Media" onClick={fetchLatestMedia}>
                    <i className="fas fa-sync-alt"></i>
                </button>
            </div>
            <Swiper className="mySwiper mt-2 px-2"
                slidesPerView={isDesktop ? 2.2 : 1.2}
                spaceBetween={10}
                freeMode={true}
            >
                {
                    posts.map((post, i) => {
                        return (
                            <SwiperSlide key={i}>
                                <div className="card card-style m-0 mx-1 shadow-none border p-3" onClick={() => setSelectedPost(post)}>
                                    <img src={post.thumbnail_url} className="rounded-4 post-thumbnail" alt="feed" loading='lazy' />
                                    <div className="swiper-lazy-preloader"></div>
                                    <div className="card-body text-start">
                                        <span className="d-block text-muted mb-1 font-10">{post.posted_on}</span>
                                        <p style={{ height: '50px' }}>{post.ig?.caption?.slice(0, 65)}{(post?.ig?.caption?.length ?? 0) > 65 ? '...' : ''}</p>
                                    </div>
                                    <button className="btn btn-primary-outlined rounded-sm btn-sm w-100" onClick={() => setSelectedPost(post)}>
                                        <i className="fa-solid fa-link me-2"></i> Add Link
                                    </button>
                                </div>
                            </SwiperSlide>
                        )
                    })
                }
                {
                    posts.length > 0 &&
                    <div className='mb-4'></div>
                }
                {
                    !loading && posts.length === 0 &&
                    <div className="card h-full card-style rounded-card mx-2">
                        <div className="card-body">
                            <div className="h-200px d-flex justify-content-center align-items-center text-center">
                                <div className='px-5'>
                                    <h2 className="pop">No content available to add a Smart link.</h2>
                                </div>
                            </div>
                        </div>
                    </div>
                }
            </Swiper>
            <AddLink
                isActive={!!selectedPost}
                onClose={() => setSelectedPost(null)}
                post={selectedPost}
                onError={(error) => {
                    setSelectedPost(null)
                    setLinkingError(error)
                }}
            />
            <InsufficientPermissionsCTAModal
                isActive={linkingError?.error_code === ERROR.INSUFFICIENT_PERMISSIONS}
                onClose={() => setLinkingError(null)}
                missing_permissions={linkingError?.missing_permissions!}
                feature={linkingError?.feature!}
            />
        </div>
    )
}

export default AvailableToLink